/**
 * A downsizing plan in everyday words — the card's title and the line under it, the money each plan frees
 * and where it goes (downsizing rebuild, 21 Oct 2026; the agreed mock-up's plans screen).
 *
 * The engine's names ("Downsize, top up super (downsizer), and start a pension from it") were written for an
 * adviser's list. The card says what is bought (or that they rent), at what price, and where the money the
 * sale frees goes — "Buy a $1.96 million home, put $600,000 into super and draw it as a pension". Read from
 * the candidate's own inputs with the module's one sale arithmetic (dzSaleFigures.ts), so the words, the
 * plan page and the advice document quote the same figures. Presentation only: nothing here is computed
 * beyond sums.
 */
import { formatCurrency } from '../../utils/index.ts';
import { investedWhereWords } from '../../utils/investedWhere.ts';
import type { DraftFactFind } from '../../constants/initialFactFind.ts';
import { dzSaleFigures } from './dzSaleFigures.ts';
import { dzPriceWords, type DzMoveRecord } from './dzChosenPlan.ts';
import { rvDrawWords } from '../retirementVillage/rvPlanWords.ts';

export interface DzCandidateLike {
    id?: string | null;
    name?: string;
    inputs?: Record<string, unknown> | null;
    rentPerWeek?: number;
    [key: string]: unknown;
}

const num = (v: unknown) => {
    const n = typeof v === 'number' ? v : parseFloat(String(v ?? ''));
    return Number.isFinite(n) ? n : 0;
};
const money = (n: number) => formatCurrency(Math.round(n));
const joinAnd = (xs: string[]) => (xs.length <= 1 ? xs[0] || '' : `${xs.slice(0, -1).join(', ')} and ${xs[xs.length - 1]}`);

/** The sale and the new home of a plan, as its inputs say — the one reading every screen uses. */
export function dzMoveOfInputs(inputs: Record<string, unknown> | null | undefined, extra: { rentPerWeek?: number; homeName?: string; date?: string } = {}): DzMoveRecord {
    const d = inputs || {};
    const salePrice = num(d.salePrice);
    const sellingCosts = num(d.sellingCosts);
    const newHomePrice = num(d.newHomePurchasePrice);
    const sellAndRent = d.rentInstead === true || (newHomePrice === 0 && salePrice > 0);
    const { stampDuty, freedCapital, buyUpShortfall } = dzSaleFigures({ salePrice, sellingCosts, newHomePrice, stampDuty: num(d.stampDuty), sellAndRent });
    return {
        salePrice, sellingCosts, newHomePrice: sellAndRent ? 0 : newHomePrice, stampDuty,
        state: String(d.newHomeState || ''),
        sellAndRent,
        rentPerWeek: sellAndRent ? num(extra.rentPerWeek ?? d.rentPerWeek) : 0,
        freedCapital, buyUpShortfall,
        homeName: String(extra.homeName || ''),
        date: String(extra.date || ''),
    };
}

/** Where the money the sale frees goes, each amount, and what stays in the bank. */
export interface DzUses {
    downsizerClient: number;
    downsizerPartner: number;
    pensionFromDownsizer: boolean;
    afterTaxSuper: number;
    pensionFromAfterTax: boolean;
    annuity: number;
    annuityType: string;
    annuityResidualPercent: number;
    investmentProperty: number;
    ipStampDuty: number;
    investmentBond: number;
    funeralBond: number;
    gift: number;
    shares: number;
    /** Everything placed (the property with its duty). */
    placed: number;
    /** What the sale frees that is not placed: it stays in the bank. */
    bank: number;
}

export function dzUsesOf(inputs: Record<string, unknown> | null | undefined): DzUses {
    const d = inputs || {};
    const dep = (d.deployment || {}) as Record<string, unknown>;
    const move = dzMoveOfInputs(d);
    const u = {
        downsizerClient: num(d.downsizerContribution),
        downsizerPartner: num(d.partnerDownsizerContribution),
        pensionFromDownsizer: d.commenceIncomeStream === true,
        afterTaxSuper: num(dep.superContribution) + num(dep.partnerSuperContribution),
        pensionFromAfterTax: dep.superCommenceIncomeStream === true,
        annuity: num(dep.annuity),
        annuityType: String(dep.annuityType || 'lifetime'),
        annuityResidualPercent: num(dep.annuityResidualPercent),
        investmentProperty: num(dep.investmentProperty),
        ipStampDuty: num(dep.ipStampDuty),
        investmentBond: num(dep.investmentBond),
        funeralBond: num(d.funeralBondPurchase),
        gift: num(d.giftingAmount),
        shares: num(dep.shares),
    };
    const placed = u.downsizerClient + u.downsizerPartner + u.afterTaxSuper + u.annuity + u.investmentProperty + u.ipStampDuty + u.investmentBond + u.funeralBond + u.gift + u.shares;
    return { ...u, placed, bank: Math.max(0, move.freedCapital - placed) };
}

/** "$3.2m" — a price on a goal's sub-line. */
export function dzShortPrice(price: number): string {
    const p = Math.max(0, Math.round(price));
    return p >= 1_000_000 ? `$${String((Math.round(p / 100_000) / 10).toFixed(1)).replace(/\.0$/, '')}m` : `$${Math.round(p / 1000)}k`;
}

/** The home a plan buys, in a few words: "A dearer home, $3.2m", "A home at a similar price", "Renting". */
export function dzHomeShort(m: Pick<DzMoveRecord, 'sellAndRent' | 'newHomePrice' | 'salePrice' | 'sellingCosts'>): string {
    if (m.sellAndRent) return 'Selling and renting';
    const net = m.salePrice - m.sellingCosts;
    if (Math.abs(m.newHomePrice - net) <= m.salePrice * 0.05) return `A home at a similar price, ${dzShortPrice(m.newHomePrice)}`;
    if (m.newHomePrice > net) return `A dearer home, ${dzShortPrice(m.newHomePrice)}`;
    return `A ${dzShortPrice(m.newHomePrice)} home`;
}

/** "the money back in the early years" — a guaranteed income said by what the family receives. */
function annuityBack(u: DzUses): string {
    if (u.annuityType === 'guaranteed') return 'that pays the full amount back to the family at any time';
    if (u.annuityType === 'fixedTerm') return 'for a set term';
    return u.annuityResidualPercent > 0 ? 'that pays the money back to the family in the early years' : 'that pays the most income, with nothing back to the family';
}

/** The card's title and sub-line for a plan. */
export function dzPlanWords(row: DzCandidateLike, factFind: DraftFactFind): { title: string; sub: string } {
    const inputs = (row.inputs || {}) as Record<string, unknown>;
    const id = String(row.id || '');
    const m = dzMoveOfInputs(inputs, { rentPerWeek: row.rentPerWeek });
    const u = dzUsesOf(inputs);
    const clientName = factFind?.client?.firstName || 'you';
    const partnerName = factFind?.partner?.firstName || 'your partner';
    const debts = /-clear-debt$/.test(id);

    // The home.
    const similar = !m.sellAndRent && m.newHomePrice > 0 && Math.abs(m.newHomePrice - (m.salePrice - m.sellingCosts)) <= m.salePrice * 0.05;
    const dearer = !m.sellAndRent && !similar && m.newHomePrice > m.salePrice - m.sellingCosts;
    const home = m.sellAndRent
        ? `Sell and rent${m.rentPerWeek > 0 ? ` at ${money(m.rentPerWeek)} a week` : ''}`
        : similar ? `Buy a home at a similar price, about ${dzPriceWords(m.newHomePrice)}`
            : dearer ? `Buy a dearer home, about ${dzPriceWords(m.newHomePrice)}`
                : `Buy a ${dzPriceWords(m.newHomePrice)} home`;

    // Where the money goes, short for the title and long for the sub-line.
    const short: string[] = [];
    const long: string[] = [];
    const ds = u.downsizerClient + u.downsizerPartner;
    if (ds > 0) {
        short.push(`put ${money(ds)} into super${u.pensionFromDownsizer ? ' and draw it as a pension' : ''}`);
        const into = u.downsizerClient > 0 && u.downsizerPartner > 0
            ? (Math.round(u.downsizerClient) === Math.round(u.downsizerPartner)
                ? `${money(u.downsizerClient)} each goes into super as a downsizer contribution`
                : `${money(u.downsizerClient)} goes into ${clientName}'s super and ${money(u.downsizerPartner)} into ${partnerName}'s, each as a downsizer contribution`)
            : `${money(ds)} goes into ${u.downsizerClient > 0 ? clientName : partnerName}'s super as a downsizer contribution`;
        long.push(`${into}${u.pensionFromDownsizer ? ', drawn as a pension' : ''}`);
    }
    if (u.afterTaxSuper > 0) {
        short.push(`put ${money(u.afterTaxSuper)}${ds > 0 ? ' more' : ''} into super after tax`);
        long.push(`${money(u.afterTaxSuper)} into super as an after-tax contribution${u.pensionFromAfterTax ? ', drawn as a pension' : ''}`);
    }
    if (u.annuity > 0) {
        short.push(`put ${money(u.annuity)} into a guaranteed income`);
        long.push(`${money(u.annuity)} into a guaranteed income for life ${annuityBack(u)}`);
    }
    if (u.investmentProperty > 0) {
        short.push('buy an investment property');
        long.push(`${money(u.investmentProperty)} on an investment property${u.ipStampDuty > 0 ? `, plus ${money(u.ipStampDuty)} of stamp duty` : ''}`);
    }
    if (u.investmentBond > 0) { short.push(`put ${money(u.investmentBond)} into an investment bond`); long.push(`${money(u.investmentBond)} into an investment bond`); }
    if (u.funeralBond > 0) { short.push(`put ${money(u.funeralBond)} into funeral bonds`); long.push(`${money(u.funeralBond)} into funeral bonds, outside the assets test`); }
    if (u.gift > 0) { short.push(`give ${money(u.gift)} to family`); long.push(`${money(u.gift)} given to family`); }
    if (u.shares > 0) {
        short.push(short.length ? 'invest the rest' : `invest the ${money(u.shares)}`);
        long.push(`${short.length > 1 ? 'the other ' : ''}${money(u.shares)} invested ${investedWhereWords(factFind)}`);
    }
    if (!short.length && m.freedCapital >= 1) short.push('keep the rest in the bank');

    const title = `${home}${short.length ? `, ${joinAnd(short)}` : ''}${debts ? ', and clear the debts' : ''}`;

    // The sub-line: what the sale leaves, then where it goes.
    const parts: string[] = [];
    if (m.sellAndRent) {
        parts.push(`The sale nets ${money(m.salePrice - m.sellingCosts)} and nothing is bought; the rent${m.rentPerWeek > 0 ? ` of ${money(m.rentPerWeek)} a week` : ''} is paid from the money, with Rent Assistance where it applies.`);
    } else if (m.buyUpShortfall >= 1) {
        const draw = rvDrawWords((inputs.buyUpFunding || null) as Record<string, unknown> | null, factFind, 'the top-up');
        parts.push(similar
            ? `The whole sale goes into the new home; the ${money(m.stampDuty)} of stamp duty comes from savings. Nothing is freed up.`
            : `The whole sale and ${money(m.buyUpShortfall)} of savings go into the new home${m.stampDuty > 0 ? ` and its ${money(m.stampDuty)} of stamp duty` : ''}. Nothing is freed up.${draw ? ` ${draw.replace(/^./, (c) => c.toUpperCase())}.` : ''}`);
    } else if (m.freedCapital >= 1) {
        parts.push(`The sale frees ${money(m.freedCapital)}${m.stampDuty > 0 ? ` after ${money(m.stampDuty)} of stamp duty` : ''}.`);
    } else {
        parts.push('The whole sale goes into the new home. Nothing is freed up.');
    }
    if (!m.sellAndRent && m.newHomePrice > 0 && m.stampDuty <= 0) parts.push('No stamp duty is in the figures: no state was chosen.');
    if (long.length) parts.push(`${long.join('; ').replace(/^./, (c) => c.toUpperCase())}${u.bank >= 1 ? `; ${money(u.bank)} stays in the bank` : ''}.`);
    else if (m.freedCapital >= 1) parts.push(`The ${money(m.freedCapital)} stays in the bank.`);
    if (debts) parts.push('The debts are cleared at the same time.');
    return { title, sub: parts.join(' ') };
}

/**
 * The money the sale frees, and where it goes, as the plans and the document state it: the evaluator's own
 * rows, with what stays in the bank worked out after the stamp duty and any funeral bond (the evaluator's
 * "Kept in bank" row leaves both in it — a display slip, not the projection's).
 */
export function dzWhereTheMoneyGoes(inputs: Record<string, unknown> | null | undefined, breakdown: Array<{ label: string; amount: number }> | undefined): Array<{ label: string; amount: number }> {
  const rows = (breakdown || []).filter((r) => !/^kept in bank$/i.test(r.label) && !/^rent \(/i.test(r.label) && num(r.amount) > 0);
  const u = dzUsesOf(inputs);
  const m = dzMoveOfInputs(inputs);
  if (m.stampDuty > 0 && !m.sellAndRent) rows.splice(rows.findIndex((r) => /^new home$/i.test(r.label)) + 1, 0, { label: 'Stamp duty on the new home', amount: m.stampDuty });
  if (u.funeralBond > 0 && !rows.some((r) => /funeral/i.test(r.label))) rows.push({ label: 'Funeral bonds', amount: u.funeralBond });
  if (u.bank >= 1) rows.push({ label: 'Kept in the bank', amount: u.bank });
  return rows;
}
